/**
 * Services slider, from hung-hola blocks/services/view.js (Flickity 2.3 there, Flickity 3 here).
 * - A plain Flickity slider at every width (round 1 #5, 2026-10-22; it replaced the sticky row run by the page scroll):
 *   cellAlign center, no ends (wrapAround), a drag or the ‹ › buttons (and the arrow keys) move it.
 * - Autoplay: the next card every AUTOPLAY ms, also while the mouse is over the row (owner, 2026-10-22); held during a
 *   drag, while keyboard focus is inside, while the popup is open and while the row is off screen; any move restarts
 *   the count.
 * - The bar fills with Flickity's scroll progress, from the first card (0) to the last (100%).
 * - Only "View Detail" is a link (new tab). Above 1280px with a mouse it shows on hover; at 1280px and below and on touch
 *   screens tapping a card opens it in a popup (Easy Popup) in its hover state, with a close button.
 * - Each photo moves against the row (parallax, updateParallax below).
 * - Desktop starts on the middle card (the Figma frame), smaller screens on the first.
 * - ?still and reduced motion: no autoplay and no parallax; a drag and the buttons still move the row (Flickity's glide
 *   off: it jumps).
 * Motion is Flickity's own physics: tune SELECTED_ATTRACTION / FRICTION here (Flickity defaults).
 * Easy Popup stops and starts window.lenis itself while a popup is open. It is the shared "px-easy-popup" script
 * (block.json viewScript, assets/js/easy-popup.entry.ts), not bundled here.
 * Undo: this block was rebuilt in one commit (its hash in .claude/docs/frontend.md): git revert it, pnpm build, re-sync
 * the legacy homepage plugin.
 */
import { getEasyPopup } from '../../assets/js/easy-popup-global';
import { getFlickity } from '../../assets/js/flickity-global';
import { isStill } from '../../assets/js/motion';

// Flickity ships no types: the parts used here
interface FlickityCell {
	element: HTMLElement;
}
interface FlickityInstance {
	selectedIndex: number;
	cells: FlickityCell[];
	viewport: HTMLElement;
	previous: (isWrap?: boolean) => void;
	next: (isWrap?: boolean) => void;
	select: (index: number, isWrap?: boolean, isInstant?: boolean) => void;
	on: (event: string, listener: (...args: never[]) => void) => void;
}
type FlickityClass = (new (element: Element, options: Record<string, unknown>) => FlickityInstance) & {
	Cell: { prototype: { unselect: (this: FlickityCell) => void } };
};

// Flickity is the shared "px-flickity" script (block.json viewScript), not bundled here; without it the cards stay a plain row
const FlickityCtor = getFlickity<FlickityClass>();

// Flickity marks every card but the selected one aria-hidden, and they hold a focusable link (axe: aria-hidden-focus);
// every card is on screen or one press away, so none is hidden from screen readers. Flickity is shared now: only the
// Services cards, other sliders keep Flickity's own unselect
if (FlickityCtor) {
	const unselect = FlickityCtor.Cell.prototype.unselect;
	FlickityCtor.Cell.prototype.unselect = function () {
		if (this.element.closest('[data-px-block="services"]')) {
			this.element.classList.remove('is-selected');
		} else {
			unselect.call(this);
		}
	};
}

const SELECTED_ATTRACTION = 0.025; // higher = faster to the target (Flickity default 0.025)
const FRICTION = 0.28; // higher = less bounce (Flickity default 0.28)
const AUTOPLAY = 3000; // ms between two cards (client, round 1 #5)
// Image parallax (CodeGrid "infinite parallax slider", updateParallax): each photo shifts against the row by PARALLAX x
// its card's distance from the row centre, at scale PARALLAX_SCALE. Sideways only.
const PARALLAX = -0.15;
const PARALLAX_SCALE = 1.1;
// The photos are lazy (render.php); the row moves sideways inside a hidden overflow, so the browser would fetch each one
// only as it slides in. All of them load once the section is this far below (or above) the screen, so none is still
// loading when it shows
const PRELOAD_MARGIN = '150% 0px';

const DESKTOP = '(width > 1280px)';
const DESKTOP_HOVER = '(hover: hover) and (width > 1280px)';
// Hover intent: a card turns only once the mouse has slowed down on it: less than HOVER_SLACK px moved (against the
// card, so a row sliding under a still mouse counts as moving) over HOVER_DELAY ms. A mouse sweeping over the row
// lights no card, however long it takes to cross one.
const HOVER_DELAY = 20; // ms between two checks (was 50, owner 2026-10-22: quicker)
const HOVER_SLACK = 4; // px the mouse may move against the card between two checks (4 / 20ms = 200px/s)

document.querySelectorAll<HTMLElement>('[data-px-block="services"]').forEach((wrapper) => {
	const track = wrapper.querySelector<HTMLElement>('[data-services-track]');
	if (!track || !FlickityCtor) {
		return;
	}

	const prev = wrapper.querySelector<HTMLButtonElement>('[data-services-prev]');
	const next = wrapper.querySelector<HTMLButtonElement>('[data-services-next]');
	const bar = wrapper.querySelector<HTMLElement>('[data-services-bar]');
	const stage = wrapper.querySelector<HTMLElement>('.services__stage');
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	const cardCount = track.children.length;
	const images = [...track.querySelectorAll<HTMLImageElement>('.services__image')];
	const still = isStill(); // ?still or reduced motion: no autoplay, no parallax

	if ('IntersectionObserver' in window) {
		const preload = new IntersectionObserver((entries) => {
			if (entries.some((entry) => entry.isIntersecting)) {
				images.forEach((img) => (img.loading = 'eager'));
				preload.disconnect();
			}
		}, { rootMargin: PRELOAD_MARGIN });
		preload.observe(wrapper);
	} else {
		images.forEach((img) => (img.loading = 'eager'));
	}

	let reach = 0; // distance at which a card only just touches the row edge

	const layoutParallax = (): void => {
		const card = images[0]?.parentElement;
		if (still || !card) {
			return;
		}
		reach = track.offsetWidth / 2 + card.offsetWidth / 2;
		const shift = Math.ceil(Math.abs(PARALLAX) * reach);
		const height = card.offsetHeight;
		// The photo box grows by the largest shift on each side, or to the photo's own width at that height when wider
		// (what object-cover already shows), so a photo cut to the box ratio moves without extra zoom
		images.forEach((img) => {
			const ratio = Number(img.getAttribute('width')) / Number(img.getAttribute('height')) || 1;
			const extra = Math.max(shift, (height * ratio - card.offsetWidth) / 2);
			img.style.left = `${-extra}px`;
			img.style.top = '0px';
			img.style.width = `${card.offsetWidth + 2 * extra}px`;
			img.style.height = `${height}px`;
			img.style.maxWidth = 'none';
		});
	};

	const updateParallax = (): void => {
		if (still) {
			return;
		}
		const row = track.getBoundingClientRect();
		if (row.bottom < 0 || row.top > window.innerHeight) {
			return;
		}
		const center = row.left + row.width / 2;
		images.forEach((img) => {
			const card = img.parentElement!.getBoundingClientRect();
			const distance = Math.max(-reach, Math.min(reach, card.left + card.width / 2 - center));
			img.style.transform = `translateX(${distance * PARALLAX}px) scale(${PARALLAX_SCALE})`;
		});
	};

	const flkty = new FlickityCtor(track, {
		cellAlign: 'center',
		wrapAround: true,
		initialIndex: window.matchMedia(DESKTOP).matches ? Math.floor((cardCount - 1) / 2) : 0,
		prevNextButtons: false,
		pageDots: false,
		percentPosition: false, // whole px: cards land exactly where the static layout puts them
		selectedAttraction: reduceMotion ? 1 : SELECTED_ATTRACTION,
		friction: reduceMotion ? 1 : FRICTION,
		on: {
			// progress runs 0 (first card) to 1 (last card) and keeps counting with wrapAround: one turn is n / (n - 1)
			// (the last card back to the first), the bar full from the last card until the row wraps to the first
			scroll: (progress: number) => {
				if (bar) {
					const turn = cardCount > 1 ? cardCount / (cardCount - 1) : 1;
					bar.style.width = `${Math.min(1, ((progress % turn) + turn) % turn) * 100}%`;
				}
				updateParallax();
			},
		},
	});
	const refreshParallax = (): void => {
		layoutParallax();
		updateParallax();
	};
	refreshParallax();
	flkty.on('resize', refreshParallax);

	prev?.addEventListener('click', () => flkty.previous(true));
	next?.addEventListener('click', () => flkty.next(true));

	// Touch screens and 1280px and below: a tap opens the card in this popup (Easy Popup, the end of this file)
	const popupId = stage?.dataset.servicesPopup;
	const popup = getEasyPopup(popupId);

	// Autoplay: a timer restarted on every move; `holds` lists why it waits (drag, keyboard focus, off screen)
	const holds = new Set<string>();
	let autoTimer = 0;
	const popupOpen = (): boolean => Boolean(popup?.isOpen);
	const schedule = (): void => {
		window.clearTimeout(autoTimer);
		if (still || cardCount < 2 || holds.size) {
			return;
		}
		autoTimer = window.setTimeout(() => {
			if (!popupOpen()) {
				flkty.next(true);
			}
			schedule();
		}, AUTOPLAY);
	};
	const hold = (why: string, on: boolean): void => {
		if (on) {
			holds.add(why);
		} else {
			holds.delete(why);
		}
		schedule();
	};
	flkty.on('select', schedule);
	// Flickity's own pointer events: a press (mouse, touch, pen) holds until it is let go
	flkty.on('pointerDown', () => hold('drag', true));
	flkty.on('pointerUp', () => hold('drag', false));
	// Keyboard focus only: a click on ‹ › or a link focuses it too, and must not stop the row
	wrapper.addEventListener('focusin', (e) => hold('focus', (e.target as HTMLElement).matches(':focus-visible')));
	wrapper.addEventListener('focusout', (e) => !wrapper.contains(e.relatedTarget as Node | null) && hold('focus', false));
	if ('IntersectionObserver' in window) {
		hold('offscreen', true);
		new IntersectionObserver((entries) => hold('offscreen', !entries[entries.length - 1].isIntersecting)).observe(track);
	}

	// A click at the end of a drag opens no popup and follows no link
	let dragged = false;
	flkty.on('pointerDown', () => (dragged = false));
	flkty.on('dragStart', () => (dragged = true));
	track.addEventListener(
		'click',
		(e) => {
			if (dragged) {
				dragged = false;
				e.preventDefault();
				e.stopPropagation();
			}
		},
		true
	);

	// Tabbing to a card off screen: bring it in instead of letting the viewport scroll; a card already whole on screen
	// stays put (tabbing through the visible cards moves nothing)
	track.addEventListener('focusin', (e) => {
		const index = flkty.cells.findIndex((cell) => cell.element.contains(e.target as Node));
		if (index > -1) {
			const card = flkty.cells[index].element.getBoundingClientRect();
			const row = track.getBoundingClientRect();
			if (card.left < row.left || card.right > row.right) {
				flkty.select(index);
			}
		}
		flkty.viewport.scrollLeft = 0;
	});

	// Hover above 1280px with a mouse: .is-hovered (the card-on: variant, services.css) once the mouse has slowed down
	// on the card (hover intent, HOVER_DELAY / HOVER_SLACK); leaving drops it at once. The white circle grows from the side the mouse came in by and shrinks
	// into the side it left by, at the mouse's height (--card-reveal-x / -y)
	const desktopHover = window.matchMedia(DESKTOP_HOVER);
	const aim = (card: HTMLElement, e: PointerEvent): void => {
		const box = card.getBoundingClientRect();
		card.style.setProperty('--card-reveal-x', e.clientX < box.left + box.width / 2 ? '0%' : '100%');
		card.style.setProperty('--card-reveal-y', `${Math.round(e.clientY - box.top)}px`);
	};
	track.querySelectorAll<HTMLElement>('.services__card').forEach((card) => {
		let timer = 0;
		let mouse = { x: 0, y: 0 }; // last pointer position on screen
		// the pointer's place on the card: moves when the mouse moves or when the card slides under it
		const onCard = (): { x: number; y: number } => {
			const box = card.getBoundingClientRect();
			return { x: mouse.x - box.left, y: mouse.y - box.top };
		};
		card.addEventListener('pointermove', (e) => {
			mouse = { x: e.clientX, y: e.clientY };
		});
		card.addEventListener('pointerenter', (e) => {
			if (e.pointerType !== 'mouse' || !desktopHover.matches) {
				return;
			}
			mouse = { x: e.clientX, y: e.clientY };
			let last = onCard();
			const check = (): void => {
				const now = onCard();
				if (Math.hypot(now.x - last.x, now.y - last.y) > HOVER_SLACK) {
					last = now; // still moving: look again
					timer = window.setTimeout(check, HOVER_DELAY);
					return;
				}
				if (!card.classList.contains('is-hovered')) {
					aim(card, e); // from the side the mouse came in by
				}
				card.classList.add('is-hovered');
			};
			window.clearTimeout(timer);
			timer = window.setTimeout(check, HOVER_DELAY);
		});
		card.addEventListener('pointerleave', (e) => {
			window.clearTimeout(timer);
			if (card.classList.contains('is-hovered')) {
				aim(card, e);
				card.classList.remove('is-hovered');
			}
		});
	});

	// At 1280px and below and on touch screens a tap opens the card in a popup (Easy Popup) instead of hovering
	if (!popup) {
		return;
	}

	track.addEventListener('click', (e) => {
		const target = e.target as HTMLElement;
		const card = target.closest<HTMLElement>('.services__card');
		// The "View Detail" link (new tab) is only clickable once the card shows it: let it through
		if (!card || desktopHover.matches || target.closest('.services__link')) {
			return;
		}
		const copy = card.cloneNode(true) as HTMLElement;
		copy.removeAttribute('data-px-fade-in-item');
		copy.removeAttribute('style');
		copy.classList.remove('is-hovered');
		copy.querySelectorAll('img[loading]').forEach((img) => img.removeAttribute('loading'));
		copy.querySelectorAll('.services__image').forEach((img) => img.removeAttribute('style')); // no parallax in the popup
		popup.el.querySelector('.services__card')?.remove();
		popup.el.prepend(copy);
		popup.open();
		// Switch to the hover state two frames later so the popup plays the same motion as a mouse hover
		requestAnimationFrame(() => requestAnimationFrame(() => copy.classList.add('is-open')));
	});
	desktopHover.addEventListener('change', () => popup.isOpen && popup.close());
});
